import React, { FC, useEffect, useState, useContext } from "react"
import { Form, Select, Button, Table, Space, Row, Col } from "antd"
import type { ColumnsType } from 'antd/es/table';
import { store } from '@root/store';
import { ChinaProvincePorps } from '@root/actions';
import { statisticsOfProince, statisticsOfCity, exportStatisticsOfProince, exportStatisticsOfCity } from '@root/common/service';
import { YearSelect, MonthSelect } from '@root/components/selectBox';
import { StatisticsContext, TotalViewProps, DateProps } from '../index';
const { Option } = Select;

// 当前查询类型：省 | 市
type Level = 'province' | 'city';

// 按省查询：列表内容数据类型
interface ProvinceItemProps {
  averageWatchTime: number,
  courseCount: number,
  courseDuration: number,
  index: string,
  provinceCode: string,
  provinceName: string,
  recipientHospitalCourseCount: number,
  supportHospitalCourseCount: number,
  watchTime: number,
  watchUserCount: number
}

// 按市查询：列表内容数据类型
interface CityItemProps {
  averageWatchTime: number,
  cityCode: string,
  cityName: string,
  courseCount: number,
  courseDuration: number,
  index: string,
  provinceCode: string,
  provinceName: string,
  recipientHospitalCourseCount: number,
  supportHospitalCourseCount: number,
  watchTime: number,
  watchUserCount: number
}

// 按省查询：props数据类型
interface ProvinceViewProps {
  setLevel: React.Dispatch<React.SetStateAction<Level>>,
  setProvince: React.Dispatch<React.SetStateAction<string | undefined>>,
  getReport: (data: TotalViewProps | undefined) => string,
  setDate: React.Dispatch<React.SetStateAction<DateProps | undefined>>
}

// 按市查询：props数据类型
interface CityViewProps {
  setLevel: React.Dispatch<React.SetStateAction<Level>>,
  activeProvinced: string | undefined,
  getReport: (data: TotalViewProps | undefined) => string,
  date: DateProps | undefined,
}

// 按省查询
const ProvinceView: FC<ProvinceViewProps> = (props) => {
  const { setLevel, setProvince, getReport, setDate } = props;

  const [form] = Form.useForm();
  const context = useContext(StatisticsContext);

  const [provinceList, setProvinceList] = useState<ChinaProvincePorps[]>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableList, setTableList] = useState<ProvinceItemProps[]>([]);
  const [tableTotal, setTableTotal] = useState<number>(0);
  const [total, setTotal] = useState<TotalViewProps | undefined>()

  const columns: ColumnsType<ProvinceItemProps> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: '省份',
      dataIndex: 'provinceName',
      key: 'provinceName',
      render: (text, proince) => {
        return (
          <Button
            type="link"
            onClick={() => { onProvinceClickHandle(proince) }}
          >{text}</Button>
        )
      }
    },
    {
      title: '支援医院课程数量',
      dataIndex: 'supportHospitalCourseCount',
      key: 'supportHospitalCourseCount',
    },
    {
      title: '县医院课程数量',
      dataIndex: 'recipientHospitalCourseCount',
      key: 'recipientHospitalCourseCount',
    },
    {
      title: '课程时长/h',
      dataIndex: 'courseDuration',
      key: 'courseDuration',
    },
    {
      title: '学习人数',
      dataIndex: 'watchUserCount',
      key: 'watchUserCount',
    },
    {
      title: '学习时长/h',
      dataIndex: 'watchTime',
      key: 'watchTime',
    },
    {
      title: '平均学习时长/h',
      dataIndex: 'averageWatchTime',
      key: 'averageWatchTime',
    }
  ];

  /**
   * 获取列表内容
   * @param page 页码
   */
  const getProvinceData = (page = 1) => {
    setTableLoading(true);
    const params = form.getFieldsValue();
    statisticsOfProince(
      { pageNum: page, pageSize: 10 },
      { ...params }
    ).then(res => {
      const { data: { tableDataInfo, totalCourseProvinceView } } = res;
      setTableList(tableDataInfo.rows);
      setTableTotal(tableDataInfo.total);
      setTotal(totalCourseProvinceView)
    }).finally(() => {
      setTableLoading(false);
    });
  }

  /**
   * 点击省触发
   * @param province 省内容
   */
  const onProvinceClickHandle = (province: ProvinceItemProps) => {
    setDate({
      year: form.getFieldValue('year'),
      month: form.getFieldValue('month')
    })
    setLevel('city');
    setProvince(province.provinceCode);
  }

  // 导出
  const onExport = () => {
    let params = form.getFieldsValue();
    let uri = '?';
    if (context && context.getUrlParams) {
      uri += context.getUrlParams(params)
    }
    exportStatisticsOfProince(uri).then(res => {
      if (context && context.excelDownload) {
        context.excelDownload(res);
      }
    })
  }

  useEffect(() => {
    setProvinceList(store.getState().directionaries.chinaProvinceData);
    getProvinceData();
  }, [])

  return (
    <div className="coursestatistics-country">
      <div className="coursestatistics-form-title">
        <Form
          name="customized_form_controls"
          layout="inline"
          form={form}
          onFinish={() => { getProvinceData() }}
        >
          <Row>
            <Col span={6}>
              <Form.Item label="省份" name="provinceCode">
                <Select>
                  {
                    provinceList.map((province, index) => {
                      return (
                        <Option key={index} value={province.value}>{province.label}</Option>
                      )
                    })
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <YearSelect
                label="年度"
                name="year"
              />
            </Col>
            <Col span={6}>
              <MonthSelect
                label="月份"
                name="month"
              />
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    提交
                  </Button>
                  <Button type="primary" onClick={() => {
                    form.resetFields();
                    getProvinceData()
                  }}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="coursestatistics-export">
        <Button type="primary" onClick={onExport}>导出</Button>
      </div>
      <div className="coursestatistics-title">
        {getReport(total)}
      </div>
      <div className="coursestatistics-table">
        <Table
          rowKey="index"
          columns={columns}
          dataSource={tableList}
          loading={tableLoading}
          pagination={{
            total: tableTotal,
            onChange: (page) => { getProvinceData(page) },
            showTotal: (total) => {
              return '共' + (total || 0) + '条记录';
            },
          }}
        />
      </div>
    </div>
  )
}

// 按市查询
const CityView: FC<CityViewProps> = (props) => {
  const { date, activeProvinced, setLevel, getReport } = props;

  const [form] = Form.useForm();
  const context = useContext(StatisticsContext);

  const [cityList, setCityList] = useState<ChinaProvincePorps[]>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableList, setTableList] = useState<CityItemProps[]>([]);
  const [tableTotal, setTableTotal] = useState<number>(0);
  const [total, setTotal] = useState<TotalViewProps | undefined>()

  const columns: ColumnsType<CityItemProps> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: '省份',
      dataIndex: 'provinceName',
      key: 'provinceName',
    },
    {
      title: '所在市',
      dataIndex: 'cityName',
      key: 'cityName',
      render: (text, city) => {
        return (
          <Button
            type="link"
            onClick={() => { onCityClickHandle(city) }}
          >{text}</Button>
        )
      }
    },
    {
      title: '支援医院课程数量',
      dataIndex: 'supportHospitalCourseCount',
      key: 'supportHospitalCourseCount',
    },
    {
      title: '县医院课程数量',
      dataIndex: 'recipientHospitalCourseCount',
      key: 'recipientHospitalCourseCount',
    },
    {
      title: '课程时长/h',
      dataIndex: 'courseDuration',
      key: 'courseDuration',
    },
    {
      title: '学习人数',
      dataIndex: 'watchUserCount',
      key: 'watchUserCount',
    },
    {
      title: '学习时长/h',
      dataIndex: 'watchTime',
      key: 'watchTime',
    },
    {
      title: '平均学习时长/h',
      dataIndex: 'averageWatchTime',
      key: 'averageWatchTime',
    }
  ];

  /**
   * 获取列表内容
   * @param page 页码
   */
  const getCityData = (page = 1) => {
    setTableLoading(true);
    const params = form.getFieldsValue();
    statisticsOfCity(
      { pageNum: page, pageSize: 10 },
      { ...params, provinceCode: activeProvinced }
    ).then(res => {
      const { data: { tableDataInfo, totalCourseProvinceView } } = res;
      setTableList(tableDataInfo.rows);
      setTableTotal(tableDataInfo.total);
      setTotal(totalCourseProvinceView)
    }).finally(() => {
      setTableLoading(false);
    });
  }

  /**
   * 点击所在市列触发
   * 
   * @description 
   * 点击所在市跳转按医院查询
   * 不带年度和月份的条件，省和市的条件都需要传递
   * 
   * @param city 所在市信息
   */
  const onCityClickHandle = (city: CityItemProps) => {
    if (context && context.setkey) {
      context.setkey('3', { provinceCode: activeProvinced as string, cityCode: city.cityCode });
    }
  }

  // 点击导出触发
  const onExport = () => {
    let params = form.getFieldsValue();
    let uri = '?provinceCode=' + activeProvinced;

    if (context && context.getUrlParams) {
      uri += context.getUrlParams(params)
    }

    exportStatisticsOfCity(uri).then(res => {
      if (context && context.excelDownload) {
        context.excelDownload(res);
      }
    })
  }

  useEffect(() => {
    const selectProvince = store.getState().directionaries.chinaProvinceData.find((province: ChinaProvincePorps) => {
      return province.value === activeProvinced
    })
    if (selectProvince && selectProvince.children) {
      setCityList(selectProvince.children);
    }
    getCityData();
  }, [])

  return (
    <div className="coursestatistics-country">
      <div className="coursestatistics-form-title">
        <Form
          name="customized_form_controls"
          layout="inline"
          form={form}
          onFinish={() => { getCityData() }}
          initialValues={{
            year: date?.year,
            month: date?.month
          }}
        >
          <Row>
            <Col span={6}>
              <Form.Item label="所在市" name="cityCode">
                <Select>
                  {
                    cityList.map((city, index) => {
                      return (
                        <Option key={index} value={city.value}>{city.label}</Option>
                      )
                    })
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="year" label="年度">
                <Select>
                  <Option value={2022}>2022</Option>
                  <Option value={2023}>2023</Option>
                  <Option value={2024}>2024</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="month" label="月份">
                <Select>
                  {
                    Array.from({ length: 12 }).map((item, index) => {
                      const num = index + 1;
                      return (
                        <Option key={num} value={num}>{num}月</Option>
                      )
                    })
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    提交
                  </Button>
                  <Button type="primary" onClick={() => {
                    form.resetFields();
                    getCityData()
                  }}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="coursestatistics-export">
        <Space>
          <Button onClick={() => { setLevel('province') }}>返回</Button>
          <Button onClick={onExport} type="primary">导出</Button>
        </Space>
      </div>
      <div className="coursestatistics-title">
        {getReport(total)}
      </div>
      <div className="coursestatistics-table">
        <Table
          rowKey="index"
          columns={columns}
          dataSource={tableList}
          loading={tableLoading}
          pagination={{
            total: tableTotal,
            onChange: (page) => { getCityData(page) },
            showTotal: (total) => {
              return '共' + (total || 0) + '条记录';
            },
          }}
        />
      </div>
    </div>
  )
}

const Province: FC = () => {
  // 查询类型
  const [level, setLevel] = useState<Level>('province');
  // 当前选择省份：用于下钻查询
  const [activeProvinced, setProvince] = useState<string | undefined>();
  // 月份与年份：用于下钻查询时赋值
  const [date, setDate] = useState<DateProps>();

  // 获取合计内容
  const getReport = (data: TotalViewProps | undefined) => {
    if (data) {
      return `合计: 支援医院课程数量：${data.supportHospitalCourseCount}；支援医院课程数量：${data.recipientHospitalCourseCount}；课程时长/h：${data.courseDuration}；学习人数：${data.watchUserCount}；学习时长/h：${data.watchTime}；平均学习时长/h：${data.averageWatchTime}`
    } else {
      return ''
    }
  }

  return (
    <React.Fragment>
      {
        level === 'province' ?
          <ProvinceView
            setLevel={setLevel}
            setProvince={setProvince}
            getReport={getReport}
            setDate={setDate}
          />
          :
          <CityView
            setLevel={setLevel}
            date={date}
            activeProvinced={activeProvinced}
            getReport={getReport}
          />
      }
    </React.Fragment>
  )
}

export default Province;